"use client";

import { c as _c } from "react/compiler-runtime";
import { localPoint } from "@visx/event";
import { ParentSize } from "@visx/responsive";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Children, isValidElement, type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { BarProps } from "./bar";
import { ChartProvider, type LineConfig, type Margin, type TooltipData } from "./chart-context";
export type BarOrientation = "vertical" | "horizontal";
export interface BarChartProps {
  /** Data array - each item should have an x-axis key and numeric values */
  data: Record<string, unknown>[];
  /** Key in data for the categorical axis. Default: "name" */
  xDataKey?: string;
  /** Chart margins */
  margin?: Partial<Margin>;
  /** Animation duration in milliseconds. Default: 1100 */
  animationDuration?: number;
  /** Aspect ratio as "width / height". Default: "2 / 1" */
  aspectRatio?: string;
  /** Additional class name for the container */
  className?: string;
  /** Gap between bar groups as a fraction of band width (0-1). Default: 0.2 */
  barGap?: number;
  /** Fixed bar width in pixels. If not set, bars auto-size to fill the band. */
  barWidth?: number;
  /** Bar chart orientation. Default: "vertical" */
  orientation?: BarOrientation;
  /** Whether to stack bars instead of grouping them. Default: false */
  stacked?: boolean;
  /** Gap between stacked bar segments in pixels. Default: 0 */
  stackGap?: number;
  /** Child components (Bar, Grid, ChartTooltip, etc.) */
  children: ReactNode;
}
const DEFAULT_MARGIN: Margin = {
  top: 40,
  right: 40,
  bottom: 40,
  left: 40
};

// Extract bar configs from children synchronously
function extractBarConfigs(children: ReactNode): LineConfig[] {
  const configs: LineConfig[] = [];
  Children.forEach(children, child => {
    if (!isValidElement(child)) {
      return;
    }
    const childType = child.type as {
      displayName?: string;
      name?: string;
    };
    const componentName = typeof child.type === "function" ? childType.displayName || childType.name || "" : "";
    const props = child.props as BarProps | undefined;
    const isBarComponent = componentName === "Bar" || props && typeof props.dataKey === "string" && props.dataKey.length > 0;
    if (isBarComponent && props?.dataKey) {
      // Use stroke for tooltip dot color if provided, otherwise fall back to fill
      // This allows gradient/pattern fills to have a solid dot color
      const dotColor = props.stroke || props.fill || "var(--chart-line-primary)";
      configs.push({
        dataKey: props.dataKey,
        stroke: dotColor,
        strokeWidth: 0
      });
    }
  });
  return configs;
}

// Check if a component should render after the mouse overlay
function isPostOverlayComponent(child: ReactElement): boolean {
  const childType = child.type as {
    displayName?: string;
    name?: string;
    __isChartMarkers?: boolean;
  };
  if (childType.__isChartMarkers) {
    return true;
  }
  const componentName = typeof child.type === "function" ? childType.displayName || childType.name || "" : "";
  return componentName === "ChartMarkers" || componentName === "MarkerGroup";
}
interface ChartInnerProps {
  width: number;
  height: number;
  data: Record<string, unknown>[];
  xDataKey: string;
  margin: Margin;
  animationDuration: number;
  barGap: number;
  barWidthProp?: number;
  orientation: BarOrientation;
  stacked: boolean;
  stackGap: number;
  children: ReactNode;
  containerRef: React.RefObject<HTMLDivElement | null>;
}
function ChartInner({
  width,
  height,
  data,
  xDataKey,
  margin,
  animationDuration,
  barGap,
  barWidthProp,
  orientation,
  stacked,
  stackGap,
  children,
  containerRef
}: ChartInnerProps) {
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);
  const [timeScaleNow] = useState(() => Date.now());
  const isHorizontal = orientation === "horizontal";

  // Extract bar configs synchronously from children
  const lines = useMemo(() => extractBarConfigs(children), [children]);
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // Category accessor function - returns string for categorical scale
  const categoryAccessor = useCallback((d: Record<string, unknown>): string => {
    const value = d[xDataKey];
    if (value instanceof Date) {
      return value.toISOString();
    }
    return String(value ?? "");
  }, [xDataKey]);

  // For compatibility with ChartContext, provide a Date-based xAccessor
  const xAccessorDate = useCallback((d_0: Record<string, unknown>): Date => {
    const value_0 = d_0[xDataKey];
    if (value_0 instanceof Date) {
      return value_0;
    }
    return new Date();
  }, [xDataKey]);

  // Category scale (band) - for the categorical axis
  const categoryScale = useMemo(() => {
    const domain = data.map(d_1 => categoryAccessor(d_1));
    const range: [number, number] = isHorizontal ? [0, innerHeight] : [0, innerWidth];
    return scaleBand<string>({
      range,
      domain,
      padding: barGap
    });
  }, [innerWidth, innerHeight, data, categoryAccessor, barGap, isHorizontal]);

  // Band width for bars - use prop if provided, otherwise use scale's bandwidth
  const bandWidth = barWidthProp ?? categoryScale.bandwidth();

  // Compute max value considering stacking
  const maxValue = useMemo(() => {
    if (stacked) {
      // For stacked bars, sum all values at each data point
      let max = 0;
      for (const d_2 of data) {
        let sum = 0;
        for (const line of lines) {
          const value_1 = d_2[line.dataKey];
          if (typeof value_1 === "number") {
            sum += value_1;
          }
        }
        if (sum > max) {
          max = sum;
        }
      }
      return max || 100;
    }
    // For grouped bars, find max single value
    let max_0 = 0;
    for (const line_0 of lines) {
      for (const d_3 of data) {
        const value_2 = d_3[line_0.dataKey];
        if (typeof value_2 === "number" && value_2 > max_0) {
          max_0 = value_2;
        }
      }
    }
    return max_0 || 100;
  }, [data, lines, stacked]);

  // Value scale (linear) - for the value axis
  const valueScale = useMemo(() => {
    const range_0 = isHorizontal ? [0, innerWidth] : [innerHeight, 0];
    return scaleLinear({
      range: range_0,
      domain: [0, maxValue * 1.1],
      nice: true
    });
  }, [innerWidth, innerHeight, maxValue, isHorizontal]);

  // Compute stack offsets for stacked bars
  const stackOffsets = useMemo(() => {
    if (!stacked) {
      return undefined;
    }
    const offsets = new Map<number, Map<string, number>>();
    for (let i = 0; i < data.length; i++) {
      const d_4 = data[i];
      if (!d_4) {
        continue;
      }
      const pointOffsets = new Map<string, number>();
      let cumulative = 0;
      for (const line_1 of lines) {
        pointOffsets.set(line_1.dataKey, cumulative);
        const value_3 = d_4[line_1.dataKey];
        if (typeof value_3 === "number") {
          cumulative += value_3;
        }
      }
      offsets.set(i, pointOffsets);
    }
    return offsets;
  }, [data, lines, stacked]);

  // Column width for tooltip indicator
  const columnWidth = useMemo(() => {
    if (data.length < 1) {
      return 0;
    }
    return isHorizontal ? innerHeight / data.length : innerWidth / data.length;
  }, [innerWidth, innerHeight, data.length, isHorizontal]);

  // Pre-compute labels for ticker animation
  const dateLabels = useMemo(() => data.map(d_5 => categoryAccessor(d_5)), [data, categoryAccessor]);

  // Create a fake time scale for compatibility with ChartContext
  const fakeTimeScale = useMemo(() => {
    const now = timeScaleNow;
    const start = now - data.length * 24 * 60 * 60 * 1000;
    const scale = {
      ...categoryScale,
      domain: () => [new Date(start), new Date(now)],
      range: () => [0, innerWidth] as [number, number],
      invert: (x: number) => new Date(start + x / innerWidth * (now - start)),
      copy: () => scale
    };
    return scale;
  }, [categoryScale, innerWidth, data.length, timeScaleNow]);

  // Animation timing
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, animationDuration);
    return () => clearTimeout(timer);
  }, [animationDuration]);

  // Mouse move handler
  const handleMouseMove = useCallback((event: React.MouseEvent<SVGGElement>) => {
    const point = localPoint(event);
    if (!point) {
      return;
    }
    const pos = isHorizontal ? point.y - margin.top : point.x - margin.left;

    // Find which band the mouse is over
    const bandIndex = Math.floor(pos / columnWidth);
    const clampedIndex = Math.max(0, Math.min(data.length - 1, bandIndex));
    const d_6 = data[clampedIndex];
    if (!d_6) {
      return;
    }

    // Calculate positions for each bar
    const yPositions: Record<string, number> = {};
    const xPositions: Record<string, number> = {};
    const barPos = categoryScale(categoryAccessor(d_6)) ?? 0;
    if (isHorizontal) {
      // Horizontal bars: dots at end of bar (x = value), centered vertically in band
      const seriesCount = lines.length;
      const groupGap = seriesCount > 1 ? 4 : 0;
      const individualBarHeight = seriesCount > 0 ? (bandWidth - groupGap * (seriesCount - 1)) / seriesCount : bandWidth;
      if (stacked) {
        // Stacked horizontal: all bars same y, x at cumulative end
        let cumulative_0 = 0;
        for (const line_2 of lines) {
          const value_4 = d_6[line_2.dataKey];
          if (typeof value_4 === "number") {
            cumulative_0 += value_4;
            xPositions[line_2.dataKey] = valueScale(cumulative_0) ?? 0;
            yPositions[line_2.dataKey] = barPos + bandWidth / 2;
          }
        }
      } else {
        // Grouped horizontal: each bar at its own y position
        lines.forEach((line_3, idx) => {
          const value_5 = d_6[line_3.dataKey];
          if (typeof value_5 === "number") {
            xPositions[line_3.dataKey] = valueScale(value_5) ?? 0;
            yPositions[line_3.dataKey] = barPos + idx * (individualBarHeight + groupGap) + individualBarHeight / 2;
          }
        });
      }
    } else if (stacked) {
      // Vertical stacked bars
      let cumulative_1 = 0;
      let seriesIdx = 0;
      for (const line_4 of lines) {
        const value_6 = d_6[line_4.dataKey];
        if (typeof value_6 === "number") {
          cumulative_1 += value_6;
          const gapOffset = seriesIdx * stackGap;
          yPositions[line_4.dataKey] = (valueScale(cumulative_1) ?? 0) - gapOffset;
          seriesIdx++;
        }
      }
    } else {
      // Vertical grouped bars
      const seriesCount_0 = lines.length;
      const groupGap_0 = seriesCount_0 > 1 ? 4 : 0;
      const individualBarWidth = seriesCount_0 > 0 ? (bandWidth - groupGap_0 * (seriesCount_0 - 1)) / seriesCount_0 : bandWidth;
      lines.forEach((line_5, idx_0) => {
        const value_7 = d_6[line_5.dataKey];
        if (typeof value_7 === "number") {
          yPositions[line_5.dataKey] = valueScale(value_7) ?? 0;
          xPositions[line_5.dataKey] = barPos + idx_0 * (individualBarWidth + groupGap_0) + individualBarWidth / 2;
        }
      });
    }

    // Tooltip position: for horizontal, position at max bar end; for vertical, center of band
    let tooltipX: number;
    if (isHorizontal) {
      // Position tooltip at the end of the longest bar
      const maxX = Math.max(...Object.values(xPositions), 0);
      tooltipX = maxX;
    } else {
      tooltipX = barPos + bandWidth / 2;
    }
    setTooltipData({
      point: d_6,
      index: clampedIndex,
      x: tooltipX,
      yPositions,
      xPositions: Object.keys(xPositions).length > 0 ? xPositions : undefined
    });
    setHoveredBarIndex(clampedIndex);
  }, [categoryScale, valueScale, data, lines, margin.left, margin.top, categoryAccessor, columnWidth, bandWidth, isHorizontal, stacked, stackGap]);
  const handleMouseLeave = useCallback(() => {
    setTooltipData(null);
    setHoveredBarIndex(null);
  }, []);

  // Early return if dimensions not ready
  if (width < 10 || height < 10) {
    return null;
  }
  const canInteract = isLoaded;

  // Helper to check if a component is a gradient or pattern definition
  const isDefsComponent = (child: ReactElement): boolean => {
    const displayName = (child.type as {
      displayName?: string;
    })?.displayName || (child.type as {
      name?: string;
    })?.name || "";
    return displayName.includes("Gradient") || displayName.includes("Pattern") || displayName === "LinearGradient" || displayName === "RadialGradient";
  };

  // Separate children into defs, pre-overlay, and post-overlay
  const defsChildren: ReactElement[] = [];
  const preOverlayChildren: ReactElement[] = [];
  const postOverlayChildren: ReactElement[] = [];
  Children.forEach(children, child_0 => {
    if (!isValidElement(child_0)) {
      return;
    }
    if (isDefsComponent(child_0)) {
      defsChildren.push(child_0);
    } else if (isPostOverlayComponent(child_0)) {
      postOverlayChildren.push(child_0);
    } else {
      preOverlayChildren.push(child_0);
    }
  });
  const contextValue = {
    data,
    xScale: fakeTimeScale as unknown as ReturnType<typeof import("@visx/scale").scaleTime<number>>,
    yScale: valueScale,
    width,
    height,
    innerWidth,
    innerHeight,
    margin,
    columnWidth,
    tooltipData,
    setTooltipData,
    containerRef,
    lines,
    isLoaded,
    animationDuration,
    xAccessor: xAccessorDate,
    dateLabels,
    // Bar-specific properties
    barScale: categoryScale,
    bandWidth,
    hoveredBarIndex,
    setHoveredBarIndex,
    barXAccessor: categoryAccessor,
    orientation,
    stacked,
    stackOffsets
  };
  return <ChartProvider value={contextValue}>
      <svg aria-hidden="true" height={height} width={width}>
        {/* Gradient and pattern definitions */}
        {defsChildren.length > 0 && <defs>{defsChildren}</defs>}

        <rect fill="transparent" height={height} width={width} x={0} y={0} />

        {/* biome-ignore lint/a11y/noStaticElementInteractions: Chart interaction area */}
        <g onMouseLeave={canInteract ? handleMouseLeave : undefined} onMouseMove={canInteract ? handleMouseMove : undefined} style={{
        cursor: canInteract ? "crosshair" : "default"
      }} transform={`translate(${margin.left},${margin.top})`}>
          {/* Background rect for mouse event detection */}
          <rect fill="transparent" height={innerHeight} width={innerWidth} x={0} y={0} />

          {/* SVG children rendered before markers */}
          {preOverlayChildren}

          {/* Markers rendered last so they're on top for interaction */}
          {postOverlayChildren}
        </g>
      </svg>
    </ChartProvider>;
}
export function BarChart(t0) {
  const $ = _c(21);
  const {
    data,
    xDataKey: t1,
    margin: marginProp,
    animationDuration: t2,
    aspectRatio: t3,
    className: t4,
    barGap: t5,
    barWidth,
    orientation: t6,
    stacked: t7,
    stackGap: t8,
    children
  } = t0;
  const xDataKey = t1 === undefined ? "name" : t1;
  const animationDuration = t2 === undefined ? 1100 : t2;
  const aspectRatio = t3 === undefined ? "2 / 1" : t3;
  const className = t4 === undefined ? "" : t4;
  const barGap = t5 === undefined ? 0.2 : t5;
  const orientation = t6 === undefined ? "vertical" : t6;
  const stacked = t7 === undefined ? false : t7;
  const stackGap = t8 === undefined ? 0 : t8;
  const containerRef = useRef(null);
  let t9;
  if ($[0] !== marginProp) {
    t9 = {
      ...DEFAULT_MARGIN,
      ...marginProp
    };
    $[0] = marginProp;
    $[1] = t9;
  } else {
    t9 = $[1];
  }
  const margin = t9;
  let t10;
  if ($[2] !== className) {
    t10 = cn("relative w-full", className);
    $[2] = className;
    $[3] = t10;
  } else {
    t10 = $[3];
  }
  let t11;
  if ($[4] !== aspectRatio) {
    t11 = {
      aspectRatio
    };
    $[4] = aspectRatio;
    $[5] = t11;
  } else {
    t11 = $[5];
  }
  let t12;
  if ($[6] !== animationDuration || $[7] !== barGap || $[8] !== barWidth || $[9] !== children || $[10] !== data || $[11] !== margin || $[12] !== orientation || $[13] !== stackGap || $[14] !== stacked || $[15] !== xDataKey) {
    t12 = <ParentSize debounceTime={10}>{t13 => {
        const {
          width,
          height
        } = t13;
        return <ChartInner animationDuration={animationDuration} barGap={barGap} barWidthProp={barWidth} containerRef={containerRef} data={data} height={height} margin={margin} orientation={orientation} stacked={stacked} stackGap={stackGap} width={width} xDataKey={xDataKey}>{children}</ChartInner>;
      }}</ParentSize>;
    $[6] = animationDuration;
    $[7] = barGap;
    $[8] = barWidth;
    $[9] = children;
    $[10] = data;
    $[11] = margin;
    $[12] = orientation;
    $[13] = stackGap;
    $[14] = stacked;
    $[15] = xDataKey;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t10 || $[18] !== t11 || $[19] !== t12) {
    t13 = <div className={t10} ref={containerRef} style={t11}>{t12}</div>;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  return t13;
}
BarChart.displayName = "BarChart";
export default BarChart;